import { Inject, Injectable, Optional } from '@angular/core';
import { TrackJS } from 'trackjs';
import { TRACK_JS_METADATA_ENUM } from '../../../constants/utils/trackjs.constant';
import {
  IExceptionMetadata,
  ITrackJs,
  TrackJSConfig
} from '../../../models/utils/trackjs.interfaces';

declare const trackJs: ITrackJs;

@Injectable({ providedIn: 'root' })
export class ExceptionTrackingService {
  hostname = '';

  constructor(
    @Inject('trackJSConfig') @Optional() private trackJSConfig: TrackJSConfig[]
  ) {
    if (trackJSConfig) {
      this.hostname = document.location.hostname;
      this._setConfig(this.hostname, this.trackJSConfig);
    }
  }

  _setConfig(host: string, config: TrackJSConfig[]): boolean {
    const [env]: TrackJSConfig[] = config
      .filter(({ name }) => name === host)
      .map((x: TrackJSConfig) => {
        delete x.name;
        return x;
      });
    if (!TrackJS.isInstalled()) {
      return TrackJS.install(env);
    }
    return false;
  }

  track(exceptionStacktrace: string, exceptionMetadata: IExceptionMetadata) {
    if (window && 'trackJs' in window) {
      this._track(exceptionStacktrace, exceptionMetadata);
    } else {
      console.error(exceptionStacktrace);
    }
  }

  /**
   * Method to add page name metadata to exceptions
   * @param pageName
   */
  addPageNameMetadata(pageName: string) {
    if (typeof trackJs.addMetadata === 'function') {
      trackJs.addMetadata(TRACK_JS_METADATA_ENUM.PAGE_NAME, pageName);
    }
  }

  /**
   * Method to track exceptions along with it's metadata
   * @param exceptionStacktrace
   * @param exceptionMetadata
   * @private
   */
  private _track(
    exceptionStacktrace: string,
    exceptionMetadata: IExceptionMetadata
  ) {
    this._addMetadata(exceptionMetadata);
    this._passInformationToTrackJS(exceptionStacktrace);
    this._removeMetadata();
  }

  /**
   * Method to add fixed set of metadata in track js
   * @param exceptionMetadata
   * @private
   */
  private _addMetadata(exceptionMetadata: IExceptionMetadata) {
    if (typeof trackJs.addMetadata === 'function') {
      trackJs.addMetadata(
        TRACK_JS_METADATA_ENUM.FILE_NAME,
        exceptionMetadata.fileName
      );
      trackJs.addMetadata(
        TRACK_JS_METADATA_ENUM.METHOD_NAME,
        exceptionMetadata.methodName
      );
      trackJs.addMetadata(
        TRACK_JS_METADATA_ENUM.EXCEPTION_CATEGORY,
        exceptionMetadata.exceptionCategory
      );
      trackJs.addMetadata(
        TRACK_JS_METADATA_ENUM.EXCEPTION_MESSAGE,
        exceptionMetadata.exceptionMessage
      );
    }
  }

  /**
   * Method to remove fixed set of metadata
   * @private
   */
  private _removeMetadata() {
    if (typeof trackJs.removeMetadata === 'function') {
      trackJs.removeMetadata(TRACK_JS_METADATA_ENUM.FILE_NAME);
      trackJs.removeMetadata(TRACK_JS_METADATA_ENUM.METHOD_NAME);
      trackJs.removeMetadata(TRACK_JS_METADATA_ENUM.EXCEPTION_CATEGORY);
      trackJs.removeMetadata(TRACK_JS_METADATA_ENUM.EXCEPTION_MESSAGE);
    }
  }

  /**
   * Method to pass exception information to track js
   * @param exceptionStacktrace
   * @private
   */
  private _passInformationToTrackJS(exceptionStacktrace: string) {
    if (typeof trackJs.track === 'function') {
      trackJs.track(exceptionStacktrace);
    }
  }
}
